import { useCallback, useEffect, useState } from 'react';
import { ArrowLeftRight, Receipt, Webhook } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { EventoWebhookAdmin } from '../../api/tipos';
import {
  Aviso,
  Cartao,
  Celula,
  Esqueleto,
  Etiqueta,
  Tabela,
  Td,
  Th,
  Vazio,
} from '../../componentes/ui';
import { Colunas, Pagina, TituloPagina } from '../../componentes/Pagina';
import { dataHora, reais } from '../../util/formato';
import css from './FinanceiroDetalhe.module.css';

interface Cobranca {
  id: string;
  status: string;
  value: number;
  dueDate: string;
  externalReference?: string;
  invoiceUrl?: string;
}

interface Movimento {
  id: string;
  type: string;
  value: number;
  balance: number;
  date: string;
  description?: string;
}

/** Status do Asaas em português, com o tom da etiqueta. */
const STATUS: Record<
  string,
  { rotulo: string; tom: 'ok' | 'alerta' | 'erro' | 'neutro' }
> = {
  RECEIVED: { rotulo: 'Recebida', tom: 'ok' },
  CONFIRMED: { rotulo: 'Confirmada', tom: 'ok' },
  PENDING: { rotulo: 'Aguardando', tom: 'alerta' },
  OVERDUE: { rotulo: 'Vencida', tom: 'erro' },
  REFUNDED: { rotulo: 'Estornada', tom: 'neutro' },
  DELETED: { rotulo: 'Removida', tom: 'neutro' },
};

/**
 * O Asaas manda datas puras ("2026-10-09"). Vira "09/10/2026" sem passar por
 * `Date`, que no fuso de Brasília jogaria para o dia anterior.
 */
function dataCurta(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

function Carregando() {
  return (
    <div className={css.carregando}>
      <Esqueleto altura={20} />
      <Esqueleto altura={20} />
      <Esqueleto altura={20} />
    </div>
  );
}

export function FinanceiroDetalhe() {
  const [cobrancas, setCobrancas] = useState<Cobranca[] | null>(null);
  const [movimentos, setMovimentos] = useState<Movimento[] | null>(null);
  const [eventos, setEventos] = useState<EventoWebhookAdmin[] | null>(null);
  const [indisponivel, setIndisponivel] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const [c, m, e] = await Promise.all([
        api<{ disponivel: boolean; itens: Cobranca[] }>(
          '/admin/cobrancas?limite=20',
        ),
        api<{ disponivel: boolean; itens: Movimento[] }>(
          '/admin/extrato-asaas?limite=20',
        ),
        api<EventoWebhookAdmin[]>('/admin/webhooks?limite=30'),
      ]);
      setIndisponivel(!c.disponivel);
      setCobrancas(c.itens);
      setMovimentos(m.itens);
      setEventos(e);
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Falha ao carregar o financeiro.',
      );
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Com erro, os esqueletos ficariam girando para sempre: some com eles.
  const carregando = !erro;

  return (
    <Pagina largura="larga">
      <TituloPagina
        titulo="Financeiro"
        descricao="O dinheiro do lado do provedor: cobranças, extrato da conta Asaas e os eventos que ele nos enviou."
      />

      {erro && <Aviso>{erro}</Aviso>}
      {indisponivel && (
        <Aviso tom="alerta" titulo="Provedor de pagamento indisponível">
          O Asaas não respondeu ou não está configurado — as cobranças e o extrato não puderam ser
          consultados.
        </Aviso>
      )}

      <Colunas minimo={460} alinhar="start">
        <Cartao padding={0} titulo="Últimas cobranças">
          {!cobrancas ? (
            carregando && <Carregando />
          ) : cobrancas.length === 0 ? (
            <Vazio
              icone={<Receipt />}
              titulo="Nenhuma cobrança"
              descricao="As cobranças Pix geradas pelos clientes aparecem aqui."
            />
          ) : (
            <Tabela rotulo="Últimas cobranças">
              <thead>
                <tr>
                  <Th>Cobrança</Th>
                  <Th>Situação</Th>
                  <Th alinhar="direita">Valor</Th>
                </tr>
              </thead>
              <tbody>
                {cobrancas.map((c) => {
                  const s = STATUS[c.status] ?? {
                    rotulo: c.status,
                    tom: 'neutro' as const,
                  };
                  return (
                    <tr key={c.id}>
                      <Td>
                        <Celula
                          principal={<span className={css.codigo}>{c.id}</span>}
                          secundaria={`Vence em ${dataCurta(c.dueDate)}`}
                        />
                      </Td>
                      <Td>
                        <Etiqueta tom={s.tom}>{s.rotulo}</Etiqueta>
                      </Td>
                      <Td alinhar="direita">
                        <span className={css.valor}>{reais(Math.round(c.value * 100))}</span>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
          )}
        </Cartao>

        <Cartao padding={0} titulo="Extrato da conta Asaas">
          {!movimentos ? (
            carregando && <Carregando />
          ) : movimentos.length === 0 ? (
            <Vazio
              icone={<ArrowLeftRight />}
              titulo="Sem movimentação"
              descricao="Entradas, taxas e transferências aparecem aqui."
            />
          ) : (
            <Tabela rotulo="Extrato da conta Asaas">
              <thead>
                <tr>
                  <Th>Movimento</Th>
                  <Th alinhar="direita">Valor</Th>
                </tr>
              </thead>
              <tbody>
                {movimentos.map((m) => (
                  <tr key={m.id}>
                    <Td>
                      <Celula
                        principal={m.description ?? m.type}
                        secundaria={`${dataCurta(m.date)} · saldo ${reais(Math.round(m.balance * 100))}`}
                      />
                    </Td>
                    <Td alinhar="direita">
                      <span className={m.value >= 0 ? css.entrada : css.saida}>
                        {m.value >= 0 ? '+' : '−'} {reais(Math.abs(Math.round(m.value * 100)))}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Tabela>
          )}
        </Cartao>
      </Colunas>

      {/* ── Webhooks ──────────────────────────────────────────────────────
          A trilha para investigar "o cliente pagou e o crédito não entrou":
          mostra o que o provedor mandou e por que foi ou não aplicado. */}
      <Cartao
        padding={0}
        titulo="Eventos recebidos do Asaas"
        descricao="O que o provedor mandou e por que foi ou não aplicado."
      >
        {!eventos ? (
          carregando && <Carregando />
        ) : eventos.length === 0 ? (
          <Vazio
            icone={<Webhook />}
            titulo="Nenhum evento recebido"
            descricao="Se um cliente pagou e nada apareceu aqui, o webhook provavelmente não está cadastrado no painel do Asaas."
          />
        ) : (
          <Tabela rotulo="Eventos recebidos do Asaas">
            <thead>
              <tr>
                <Th>Evento</Th>
                <Th>Cobrança</Th>
                <Th>Resultado</Th>
                <Th>Recebido</Th>
              </tr>
            </thead>
            <tbody>
              {eventos.map((e) => (
                <tr key={e.id}>
                  <Td>
                    <span className={css.evento}>{e.evento}</span>
                  </Td>
                  <Td>
                    <span className={css.codigo}>{e.paymentId ?? '—'}</span>
                  </Td>
                  <Td>
                    {e.aplicado ? (
                      <Etiqueta tom="ok">Creditado</Etiqueta>
                    ) : (
                      <div className={css.resultado}>
                        <Etiqueta tom="neutro">Ignorado</Etiqueta>
                        {e.motivo && <span className={css.motivo}>{e.motivo}</span>}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <span className={css.data}>{dataHora(e.recebidoEm)}</span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}
      </Cartao>
    </Pagina>
  );
}
